import { twMerge } from 'tailwind-merge';
import Link from 'next/link';
import { TableCell, TableCellProps, TableRow } from '@/components/Table';
import { sumInvestments } from '@/utils/reports';
import {
  formatNumber,
  formatPercentage,
  renderPrice,
  renderProfileName,
} from '@/utils/strings';
import {
  useCompany,
  useCurrentUser,
  useInvestor,
  useInvestorCompany,
  useLatestFinancialReport,
} from '@/hooks/useStore';
import { useToggle } from '@/hooks/useToggle';
import InvestmentDialog from '@/dialogs/InvestmentDialog';
import { ArrowDownIcon, ArrowUpIcon } from '@/icons';
import CompanyInvestorOptionsCell from './CompanyInvestorOptionsCell';
import InvestmentHistoryRow from './InvestmentHistoryRow';
import { getCompanyUrl, getInvestorUrl } from '@/api/getUrl';
import { isAdmin } from '@/utils/users';
import { classes, styles } from './utils';

export type CompanyInvestorRowProps = {
  companyId: number;
  investorId: number;
  rowType: 'company' | 'investor';
};

export default function CompanyInvestorRow({
  companyId,
  investorId,
  rowType,
}: CompanyInvestorRowProps) {
  const currentUser = useCurrentUser();
  const company = useCompany(companyId);
  const investor = useInvestor(investorId);
  const investorCompany = useInvestorCompany(companyId, investorId);
  const latestReport = useLatestFinancialReport(companyId);
  const [isExpanded, toggleExpanded] = useToggle(false);
  const [isAddingNew, toggleAddingNew] = useToggle(false);

  if (!investorCompany || !investor || !company) return null; // TODO

  const { investments } = investorCompany;

  const summary = sumInvestments(
    investments.map((i) => ({ ...i, price: parseFloat(i.price) })),
    latestReport
  );

  const renderProfitLossCell = () => {
    if (!latestReport) {
      return (
        <TableCell key="profitLoss" className={classes.cell}>
          --
        </TableCell>
      );
    }
    const { deltaPrice, deltaPercent } = summary.profitLoss;
    return (
      <TableCell key="profitLoss" className={classes.cell}>
        {isNaN(deltaPrice) || !isFinite(deltaPercent) ? (
          '--'
        ) : (
          <>
            <span className={classes.price}>{renderPrice(deltaPrice)}</span>
            <span
              className={twMerge(
                classes.percentage,
                deltaPercent > 0 ? classes.positive : classes.negative
              )}>
              ({formatPercentage(deltaPercent, 1)})
            </span>
          </>
        )}
      </TableCell>
    );
  };

  const renderNameCell = () => {
    const link =
      rowType === 'company'
        ? `${getCompanyUrl(companyId)}/?companyTab=investors`
        : getInvestorUrl(investorId);
    const label =
      rowType === 'company'
        ? company.name
        : renderProfileName(investor.profile);
    return (
      <TableCell key="name" align="center" className={classes.linkCell}>
        <Link className={classes.link} href={link}>
          {label}
        </Link>
      </TableCell>
    );
  };

  const sharedProps: TableCellProps = {
    align: 'right',
    className: classes.cell,
  };

  return (
    <>
      {isAddingNew && (
        <InvestmentDialog
          companyId={companyId}
          investorId={investorId}
          onClose={() => toggleAddingNew(false)}
        />
      )}
      <TableRow key={investorId} sx={styles.row(isExpanded)}>
        <TableCell
          className={classes.expandCell}
          onClick={() => toggleExpanded()}>
          {isExpanded ? (
            <ArrowUpIcon className={classes.expandIcon} />
          ) : (
            <ArrowDownIcon className={classes.expandIcon} />
          )}
        </TableCell>
        {renderNameCell()}
        <TableCell {...sharedProps}>{formatNumber(summary.shares)}</TableCell>
        <TableCell {...sharedProps}>
          {renderPrice(summary.sharePrice)}
        </TableCell>
        <TableCell {...sharedProps}>
          {renderPrice(summary.totalInvested)}
        </TableCell>
        <TableCell {...sharedProps}>
          {renderPrice(summary.currentSharePrice)}
        </TableCell>
        <TableCell {...sharedProps}>
          {renderPrice(summary.currentValue)}
        </TableCell>
        {renderProfitLossCell()}
        {isAdmin(currentUser) ? (
          <CompanyInvestorOptionsCell
            companyId={companyId}
            investorId={investorId}
            onAddNew={() => toggleAddingNew()}
          />
        ) : (
          <TableCell />
        )}
      </TableRow>
      <InvestmentHistoryRow
        open={isExpanded}
        companyId={companyId}
        investorId={investorId}
      />
    </>
  );
}
